/**
 * Transaction process graph for the chat-offer-buy flow.
 *
 * Merges default-purchase and default-negotiation into a single
 * buyer-initiated process with Stripe pre-auth on confirm-payment and
 * provider-confirm-pickup (via OTP) or P6D auto-capture. Buyer can cancel
 * for free pre-capture; post-capture cancels deduct the Stripe fee.
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 */
export const transitions = {
  // Entry: free inquiry chat.
  INQUIRE: 'transition/inquire',

  // Entry: buyer opens negotiation with a price offer.
  CUSTOMER_MAKE_OFFER: 'transition/customer-make-offer',

  // Entry: direct buy-now.
  REQUEST_PAYMENT: 'transition/request-payment',

  // Bridges from inquiry.
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',
  MAKE_OFFER_AFTER_INQUIRY: 'transition/make-offer-after-inquiry',
  PROVIDER_DECLINE_INQUIRY: 'transition/provider-decline-inquiry',
  CUSTOMER_WITHDRAW_INQUIRY: 'transition/customer-withdraw-inquiry',
  OPERATOR_CLOSE_INQUIRY: 'transition/operator-close-inquiry',

  // Negotiation: provider responds to buyer's opening offer.
  PROVIDER_ACCEPT_CUSTOMER_OFFER: 'transition/provider-accept-customer-offer',
  PROVIDER_RESPOND_TO_CUSTOMER_OFFER: 'transition/provider-respond-to-customer-offer',
  PROVIDER_REJECT_CUSTOMER_OFFER: 'transition/provider-reject-customer-offer',
  CUSTOMER_WITHDRAW_OFFER: 'transition/customer-withdraw-offer',
  OPERATOR_REJECT_CUSTOMER_OFFER: 'transition/operator-reject-customer-offer',

  // Buyer abandons negotiation and pays list price on the same tx.
  CUSTOMER_BUY_AT_LIST_FROM_INITIAL_OFFER: 'transition/customer-buy-at-list-from-initial-offer',
  CUSTOMER_BUY_AT_LIST_FROM_COUNTER_OFFER: 'transition/customer-buy-at-list-from-counter-offer',

  // Negotiation: provider-side offer updates.
  UPDATE_OFFER: 'transition/update-offer',
  UPDATE_FROM_UPDATE_PENDING: 'transition/update-from-update-pending',
  PROVIDER_WITHDRAW_FROM_UPDATE_PENDING: 'transition/provider-withdraw-from-update-pending',
  CUSTOMER_REJECT_FROM_UPDATE_PENDING: 'transition/customer-reject-from-update-pending',
  ACCEPT_UPDATE: 'transition/accept-update',
  OPERATOR_ACCEPT_UPDATE: 'transition/operator-accept-update',

  // Negotiation: counter-offers.
  CUSTOMER_MAKE_COUNTER_OFFER: 'transition/customer-make-counter-offer',
  PROVIDER_MAKE_COUNTER_OFFER: 'transition/provider-make-counter-offer',
  PROVIDER_ACCEPT_COUNTER_OFFER: 'transition/provider-accept-counter-offer',
  PROVIDER_REJECT_COUNTER_OFFER: 'transition/provider-reject-counter-offer',
  CUSTOMER_WITHDRAW_COUNTER_OFFER: 'transition/customer-withdraw-counter-offer',
  OPERATOR_REJECT_FROM_CUSTOMER_COUNTER_OFFER:
    'transition/operator-reject-from-customer-counter-offer',

  // Negotiation: terminating an open offer.
  CUSTOMER_REJECT_OFFER: 'transition/customer-reject-offer',
  PROVIDER_WITHDRAW_OFFER: 'transition/provider-withdraw-offer',
  OPERATOR_REJECT_OFFER: 'transition/operator-reject-offer',

  // Payment funnel.
  REQUEST_PAYMENT_TO_ACCEPT_OFFER: 'transition/request-payment-to-accept-offer',
  CONFIRM_PAYMENT: 'transition/confirm-payment',
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Preauthorized: card held, 6-day clock.
  SHARE_PICKUP_ADDRESS: 'transition/share-pickup-address',
  CUSTOMER_ACTIVATE_PICKUP_CODE: 'transition/customer-activate-pickup-code',
  VERIFY_PICKUP_CODE: 'transition/verify-pickup-code',
  OPERATOR_VERIFY_PICKUP: 'transition/operator-verify-pickup',
  CUSTOMER_CANCEL_PREAUTH: 'transition/customer-cancel-preauth',
  PROVIDER_CANCEL_PREAUTH: 'transition/provider-cancel-preauth',
  OPERATOR_CANCEL_PREAUTH: 'transition/operator-cancel-preauth',
  CAPTURE_AT_INSPECTION_EXPIRE: 'transition/capture-at-inspection-expire',

  // Captured: auto-capture fired before pickup happened.
  VERIFY_PICKUP_CODE_AFTER_CAPTURE: 'transition/verify-pickup-code-after-capture',
  OPERATOR_VERIFY_PICKUP_AFTER_CAPTURE: 'transition/operator-verify-pickup-after-capture',
  CUSTOMER_CANCEL_FROM_CAPTURED: 'transition/customer-cancel-from-captured',
  OPERATOR_CANCEL_FROM_CAPTURED_FULL: 'transition/operator-cancel-from-captured-full',
  OPERATOR_CANCEL_FROM_CAPTURED_WITH_FEE: 'transition/operator-cancel-from-captured-with-fee',
  COMPLETE_CANCEL_FROM_CAPTURED_WITH_FEE: 'transition/complete-cancel-from-captured-with-fee',

  // Pickup confirmed: payout countdown until auto-release.
  AUTO_RELEASE_PAYOUT: 'transition/auto-release-payout',
  OPERATOR_RELEASE_PAYOUT: 'transition/operator-release-payout',
  AUTO_RELEASE_PAYOUT_AFTER_OPERATOR_VERIFY:
    'transition/auto-release-payout-after-operator-verify',

  // Paid out: buyer can still dispute, otherwise auto-completes.
  DISPUTE: 'transition/dispute',
  OPERATOR_DISPUTE: 'transition/operator-dispute',

  // Disputed: operator resolves.
  RESOLVE_DISPUTE_SELLER_FAVOR: 'transition/resolve-dispute-seller-favor',
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',

  // Reviews.
  AUTO_COMPLETE: 'transition/auto-complete',
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  INQUIRY_DECLINED: 'inquiry-declined',
  CUSTOMER_INITIAL_OFFER_PENDING: 'customer-initial-offer-pending',
  OFFER_PENDING: 'offer-pending',
  UPDATE_PENDING: 'update-pending',
  CUSTOMER_OFFER_PENDING: 'customer-offer-pending',
  OFFER_REJECTED: 'offer-rejected',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  CAPTURED_AFTER_INSPECTION_EXPIRY: 'captured-after-inspection-expiry',
  PICKUP_CODE_VERIFIED: 'pickup-code-verified',
  OPERATOR_VERIFIED_PICKUP: 'operator-verified-pickup',
  PAID_OUT: 'paid-out',
  DISPUTED: 'disputed',
  CANCELED: 'canceled',
  CANCEL_WITH_FEE_PENDING: 'cancel-with-fee-pending',
  COMPLETED: 'completed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
  REVIEWED: 'reviewed',
};

/**
 * Description of the transaction process graph. Mirrors process.edn at
 * ext/transaction-processes/chat-offer-buy/process.edn.
 */
export const graph = {
  id: 'chat-offer-buy/release-1',
  initial: states.INITIAL,

  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.CUSTOMER_MAKE_OFFER]: states.CUSTOMER_INITIAL_OFFER_PENDING,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },

    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        [transitions.MAKE_OFFER_AFTER_INQUIRY]: states.OFFER_PENDING,
        [transitions.PROVIDER_DECLINE_INQUIRY]: states.INQUIRY_DECLINED,
        [transitions.CUSTOMER_WITHDRAW_INQUIRY]: states.INQUIRY_DECLINED,
        [transitions.OPERATOR_CLOSE_INQUIRY]: states.INQUIRY_DECLINED,
      },
    },

    [states.INQUIRY_DECLINED]: { type: 'final' },

    [states.CUSTOMER_INITIAL_OFFER_PENDING]: {
      on: {
        [transitions.PROVIDER_ACCEPT_CUSTOMER_OFFER]: states.OFFER_PENDING,
        [transitions.PROVIDER_RESPOND_TO_CUSTOMER_OFFER]: states.OFFER_PENDING,
        [transitions.PROVIDER_REJECT_CUSTOMER_OFFER]: states.OFFER_REJECTED,
        [transitions.CUSTOMER_WITHDRAW_OFFER]: states.OFFER_REJECTED,
        [transitions.OPERATOR_REJECT_CUSTOMER_OFFER]: states.OFFER_REJECTED,
        [transitions.CUSTOMER_BUY_AT_LIST_FROM_INITIAL_OFFER]: states.PENDING_PAYMENT,
      },
    },

    [states.OFFER_PENDING]: {
      on: {
        [transitions.UPDATE_OFFER]: states.UPDATE_PENDING,
        [transitions.CUSTOMER_MAKE_COUNTER_OFFER]: states.CUSTOMER_OFFER_PENDING,
        [transitions.CUSTOMER_REJECT_OFFER]: states.OFFER_REJECTED,
        [transitions.PROVIDER_WITHDRAW_OFFER]: states.OFFER_REJECTED,
        [transitions.OPERATOR_REJECT_OFFER]: states.OFFER_REJECTED,
        [transitions.REQUEST_PAYMENT_TO_ACCEPT_OFFER]: states.PENDING_PAYMENT,
      },
    },

    [states.UPDATE_PENDING]: {
      on: {
        [transitions.UPDATE_FROM_UPDATE_PENDING]: states.UPDATE_PENDING,
        [transitions.PROVIDER_WITHDRAW_FROM_UPDATE_PENDING]: states.OFFER_REJECTED,
        [transitions.CUSTOMER_REJECT_FROM_UPDATE_PENDING]: states.OFFER_REJECTED,
        [transitions.ACCEPT_UPDATE]: states.OFFER_PENDING,
        [transitions.OPERATOR_ACCEPT_UPDATE]: states.OFFER_PENDING,
      },
    },

    [states.CUSTOMER_OFFER_PENDING]: {
      on: {
        [transitions.PROVIDER_MAKE_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.PROVIDER_ACCEPT_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.PROVIDER_REJECT_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.CUSTOMER_WITHDRAW_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.OPERATOR_REJECT_FROM_CUSTOMER_COUNTER_OFFER]: states.OFFER_REJECTED,
        [transitions.CUSTOMER_BUY_AT_LIST_FROM_COUNTER_OFFER]: states.PENDING_PAYMENT,
      },
    },

    [states.OFFER_REJECTED]: { type: 'final' },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
      },
    },

    [states.PAYMENT_EXPIRED]: { type: 'final' },

    [states.PREAUTHORIZED]: {
      on: {
        [transitions.SHARE_PICKUP_ADDRESS]: states.PREAUTHORIZED,
        [transitions.CUSTOMER_ACTIVATE_PICKUP_CODE]: states.PREAUTHORIZED,
        [transitions.VERIFY_PICKUP_CODE]: states.PICKUP_CODE_VERIFIED,
        [transitions.OPERATOR_VERIFY_PICKUP]: states.OPERATOR_VERIFIED_PICKUP,
        [transitions.CUSTOMER_CANCEL_PREAUTH]: states.CANCELED,
        [transitions.PROVIDER_CANCEL_PREAUTH]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_PREAUTH]: states.CANCELED,
        [transitions.CAPTURE_AT_INSPECTION_EXPIRE]: states.CAPTURED_AFTER_INSPECTION_EXPIRY,
      },
    },

    [states.CAPTURED_AFTER_INSPECTION_EXPIRY]: {
      on: {
        [transitions.VERIFY_PICKUP_CODE_AFTER_CAPTURE]: states.PICKUP_CODE_VERIFIED,
        [transitions.OPERATOR_VERIFY_PICKUP_AFTER_CAPTURE]: states.OPERATOR_VERIFIED_PICKUP,
        [transitions.CUSTOMER_CANCEL_FROM_CAPTURED]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_FROM_CAPTURED_FULL]: states.CANCELED,
        [transitions.OPERATOR_CANCEL_FROM_CAPTURED_WITH_FEE]: states.CANCEL_WITH_FEE_PENDING,
      },
    },

    [states.CANCEL_WITH_FEE_PENDING]: {
      on: {
        [transitions.COMPLETE_CANCEL_FROM_CAPTURED_WITH_FEE]: states.CANCELED,
      },
    },

    [states.PICKUP_CODE_VERIFIED]: {
      on: {
        [transitions.AUTO_RELEASE_PAYOUT]: states.PAID_OUT,
        [transitions.OPERATOR_RELEASE_PAYOUT]: states.PAID_OUT,
      },
    },

    [states.OPERATOR_VERIFIED_PICKUP]: {
      on: {
        [transitions.AUTO_RELEASE_PAYOUT_AFTER_OPERATOR_VERIFY]: states.PAID_OUT,
      },
    },

    [states.PAID_OUT]: {
      on: {
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.RESOLVE_DISPUTE_SELLER_FAVOR]: states.COMPLETED,
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
      },
    },

    [states.CANCELED]: { type: 'final' },

    [states.COMPLETED]: {
      on: {
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },

    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },

    [states.REVIEWED]: { type: 'final' },
  },
};

// Transitions that introduce or change an offer amount. Used to align
// transition history with metadata.offers entries.
const makeOfferTransitions = [
  transitions.CUSTOMER_MAKE_OFFER,
  transitions.MAKE_OFFER_AFTER_INQUIRY,
  transitions.PROVIDER_RESPOND_TO_CUSTOMER_OFFER,
];

const updateOfferTransitions = [
  transitions.UPDATE_OFFER,
  transitions.UPDATE_FROM_UPDATE_PENDING,
];

const counterOfferTransitions = [
  transitions.CUSTOMER_MAKE_COUNTER_OFFER,
  transitions.PROVIDER_MAKE_COUNTER_OFFER,
  transitions.PROVIDER_REJECT_COUNTER_OFFER,
  transitions.CUSTOMER_WITHDRAW_COUNTER_OFFER,
];

const offerTransitions = [
  ...makeOfferTransitions,
  ...updateOfferTransitions,
  ...counterOfferTransitions,
];

const filterOfferTransitions = (txTransitions, offerSet) =>
  txTransitions.filter(t => offerSet.includes(t.transition));

/**
 * Checks if the offers array is valid against the (past) transitions array.
 *
 * @param {Array<{transition:string, by:string, createdAt:string}>} txTransitions
 * @param {Array<{transition:string, by:string, offerInSubunits:number}>} offers
 * @returns {boolean}
 */
export const isValidNegotiationOffersArray = (txTransitions, offers) => {
  const pickedTransitions = filterOfferTransitions(txTransitions, offerTransitions);
  const isOffersAnArray = !!offers && Array.isArray(offers);

  if (!isOffersAnArray || offers.length !== pickedTransitions.length) {
    return false;
  }

  for (let i = 0; i < offers.length; i++) {
    const offer = offers[i];
    const transition = pickedTransitions[i];
    if (offer.transition !== transition.transition || offer.by !== transition.by) {
      return false;
    }
  }

  return true;
};

/**
 * Checks if the state is a negotiation state (where an offer can be made).
 *
 * @param {string} state
 * @returns {boolean}
 */
export const isNegotiationState = state => {
  if (state == null) {
    return false;
  }
  const unprefixedState = state.indexOf('/') === -1 ? state : state.split('/')[1];
  return [
    states.CUSTOMER_INITIAL_OFFER_PENDING,
    states.OFFER_PENDING,
    states.UPDATE_PENDING,
    states.CUSTOMER_OFFER_PENDING,
  ].includes(unprefixedState);
};

/**
 * Returns a copy of `txTransitions` where each offer-bearing transition
 * is annotated with the matching `offerInSubunits` from `offers`.
 *
 * @param {Array<{transition:string, by:string, createdAt:string}>} txTransitions
 * @param {Array<{transition:string, by:string, offerInSubunits:number}>} offers
 * @returns {Array} transitions, with `offerInSubunits` attached where applicable.
 */
export const getTransitionsWithMatchingOffers = (txTransitions, offers) => {
  if (!isValidNegotiationOffersArray(txTransitions, offers)) {
    return txTransitions;
  }
  const transitionsWithOffers = [];
  let offerIndex = 0;
  for (let i = 0; i < txTransitions.length; i++) {
    const transition = { ...txTransitions[i] };
    if (offerTransitions.includes(transition.transition)) {
      transition.offerInSubunits = offers[offerIndex]?.offerInSubunits;
      transitionsWithOffers.push(transition);
      offerIndex++;
    } else {
      transitionsWithOffers.push(transition);
    }
  }
  return transitionsWithOffers;
};

// Transitions that should be rendered in the ActivityFeed history.
export const isRelevantPastTransition = transition => {
  return [
    transitions.INQUIRE,
    transitions.CUSTOMER_MAKE_OFFER,
    transitions.MAKE_OFFER_AFTER_INQUIRY,
    transitions.PROVIDER_RESPOND_TO_CUSTOMER_OFFER,
    transitions.PROVIDER_REJECT_CUSTOMER_OFFER,
    transitions.CUSTOMER_WITHDRAW_OFFER,
    transitions.OPERATOR_REJECT_CUSTOMER_OFFER,
    transitions.UPDATE_OFFER,
    transitions.UPDATE_FROM_UPDATE_PENDING,
    transitions.PROVIDER_WITHDRAW_FROM_UPDATE_PENDING,
    transitions.CUSTOMER_REJECT_FROM_UPDATE_PENDING,
    transitions.ACCEPT_UPDATE,
    transitions.OPERATOR_ACCEPT_UPDATE,
    transitions.CUSTOMER_MAKE_COUNTER_OFFER,
    transitions.PROVIDER_MAKE_COUNTER_OFFER,
    transitions.PROVIDER_ACCEPT_COUNTER_OFFER,
    transitions.PROVIDER_REJECT_COUNTER_OFFER,
    transitions.CUSTOMER_WITHDRAW_COUNTER_OFFER,
    transitions.OPERATOR_REJECT_FROM_CUSTOMER_COUNTER_OFFER,
    transitions.CUSTOMER_REJECT_OFFER,
    transitions.PROVIDER_WITHDRAW_OFFER,
    transitions.OPERATOR_REJECT_OFFER,
    transitions.CONFIRM_PAYMENT,
    transitions.EXPIRE_PAYMENT,
    transitions.SHARE_PICKUP_ADDRESS,
    transitions.CUSTOMER_ACTIVATE_PICKUP_CODE,
    transitions.VERIFY_PICKUP_CODE,
    transitions.OPERATOR_VERIFY_PICKUP,
    transitions.CUSTOMER_CANCEL_PREAUTH,
    transitions.PROVIDER_CANCEL_PREAUTH,
    transitions.OPERATOR_CANCEL_PREAUTH,
    transitions.CAPTURE_AT_INSPECTION_EXPIRE,
    transitions.VERIFY_PICKUP_CODE_AFTER_CAPTURE,
    transitions.OPERATOR_VERIFY_PICKUP_AFTER_CAPTURE,
    transitions.CUSTOMER_CANCEL_FROM_CAPTURED,
    transitions.OPERATOR_CANCEL_FROM_CAPTURED_FULL,
    transitions.OPERATOR_CANCEL_FROM_CAPTURED_WITH_FEE,
    transitions.COMPLETE_CANCEL_FROM_CAPTURED_WITH_FEE,
    transitions.AUTO_RELEASE_PAYOUT,
    transitions.OPERATOR_RELEASE_PAYOUT,
    transitions.DISPUTE,
    transitions.OPERATOR_DISPUTE,
    transitions.RESOLVE_DISPUTE_SELLER_FAVOR,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.AUTO_COMPLETE,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_2_BY_CUSTOMER,
  ].includes(transition);
};

export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Privileged transitions are routed through the backend (server/api/transition-*),
// not the SDK directly. Mirrors `:privileged?` flags in process.edn.
export const isPrivileged = transition => {
  return [
    transitions.CUSTOMER_MAKE_OFFER,
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.MAKE_OFFER_AFTER_INQUIRY,
    transitions.PROVIDER_RESPOND_TO_CUSTOMER_OFFER,
    transitions.UPDATE_OFFER,
    transitions.UPDATE_FROM_UPDATE_PENDING,
    transitions.CUSTOMER_MAKE_COUNTER_OFFER,
    transitions.PROVIDER_MAKE_COUNTER_OFFER,
    transitions.PROVIDER_REJECT_COUNTER_OFFER,
    transitions.CUSTOMER_WITHDRAW_COUNTER_OFFER,
    transitions.REQUEST_PAYMENT_TO_ACCEPT_OFFER,
    transitions.CUSTOMER_BUY_AT_LIST_FROM_INITIAL_OFFER,
    transitions.CUSTOMER_BUY_AT_LIST_FROM_COUNTER_OFFER,
    transitions.CONFIRM_PAYMENT,
    transitions.CUSTOMER_ACTIVATE_PICKUP_CODE,
    transitions.VERIFY_PICKUP_CODE,
    transitions.VERIFY_PICKUP_CODE_AFTER_CAPTURE,
    transitions.CUSTOMER_CANCEL_FROM_CAPTURED,
    transitions.OPERATOR_CANCEL_FROM_CAPTURED_WITH_FEE,
  ].includes(transition);
};

// Transition lands the tx in `:state/completed` (or beyond) and triggers
// review prompts.
export const isCompleted = transition => {
  return [
    transitions.AUTO_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ].includes(transition);
};

// Transitions that issue a Stripe refund (full or partial).
export const isRefunded = transition => {
  return [
    transitions.EXPIRE_PAYMENT,
    transitions.CUSTOMER_CANCEL_PREAUTH,
    transitions.PROVIDER_CANCEL_PREAUTH,
    transitions.OPERATOR_CANCEL_PREAUTH,
    transitions.CUSTOMER_CANCEL_FROM_CAPTURED,
    transitions.OPERATOR_CANCEL_FROM_CAPTURED_FULL,
    transitions.COMPLETE_CANCEL_FROM_CAPTURED_WITH_FEE,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
  ].includes(transition);
};

// Cancel transitions available while the tx is in PREAUTHORIZED (pre-capture,
// auth released on refund, no Stripe fee burned).
export const isPreauthCancelable = transition => {
  return [
    transitions.CUSTOMER_CANCEL_PREAUTH,
    transitions.PROVIDER_CANCEL_PREAUTH,
    transitions.OPERATOR_CANCEL_PREAUTH,
  ].includes(transition);
};

// Cancel transitions that deduct the Stripe cancellation fee from the buyer's
// refund (post-capture). Used to copy "Stripe fee applies" warnings on the CTA.
export const isCapturedCancelWithFee = transition => {
  return [
    transitions.CUSTOMER_CANCEL_FROM_CAPTURED,
    transitions.OPERATOR_CANCEL_FROM_CAPTURED_WITH_FEE,
  ].includes(transition);
};

export const statesNeedingProviderAttention = [
  states.CUSTOMER_INITIAL_OFFER_PENDING,
  states.CUSTOMER_OFFER_PENDING,
  states.PREAUTHORIZED,
  states.CAPTURED_AFTER_INSPECTION_EXPIRY,
  states.PICKUP_CODE_VERIFIED,
  states.OPERATOR_VERIFIED_PICKUP,
  states.DISPUTED,
];

export const statesNeedingCustomerAttention = [
  states.OFFER_PENDING,
  states.UPDATE_PENDING,
  states.PREAUTHORIZED,
  states.CAPTURED_AFTER_INSPECTION_EXPIRY,
];
